import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Sidebar } from "./components/layout/Sidebar";
import { CreditsProvider, useCredits } from "./context/CreditsContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Loader2 } from "lucide-react";

// Pages
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Templates from "./pages/Templates";
import TemplateGenerator from "./pages/TemplateGenerator";
import TemplateEditor from "./pages/TemplateEditor";
import Campaigns from "./pages/Campaigns";
import CampaignBuilder from "./pages/CampaignBuilder";
import Sectors from "./pages/Sectors";
import Contacts from "./pages/Contacts";
import SentEmails from "./pages/SentEmails";
import Statistics from "./pages/Statistics";
import Clients from "./pages/Clients";
import Settings from "./pages/Settings";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Layout wrapper with sidebar (only for authenticated users)
const AppLayout = ({ children }) => {
  const { credits } = useCredits();
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(() => {
    return localStorage.getItem('sidebar_collapsed') === 'true';
  });

  // Listen for sidebar toggle events
  useEffect(() => {
    const handleSidebarToggle = (e) => {
      setSidebarCollapsed(e.detail.collapsed);
    };
    window.addEventListener('sidebarToggle', handleSidebarToggle);
    return () => window.removeEventListener('sidebarToggle', handleSidebarToggle);
  }, []);

  // Update credits in sidebar
  useEffect(() => {
    const balanceEl = document.querySelector('[data-testid="credits-balance"]');
    if (balanceEl) {
      balanceEl.textContent = credits.balance;
    }
  }, [credits.balance]);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <main className={`flex-1 min-h-screen transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-64'}`}>
        {children}
      </main>
    </div>
  );
};

// App content that uses auth context
const AppContent = () => {
  return (
    <CreditsProvider>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        
        {/* Protected routes */}
        <Route path="/" element={
          <ProtectedRoute>
            <AppLayout><Dashboard /></AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/templates" element={
          <ProtectedRoute>
            <AppLayout><Templates /></AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/templates/generate" element={
          <ProtectedRoute>
            <AppLayout><TemplateGenerator /></AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/templates/new" element={
          <ProtectedRoute>
            <AppLayout><TemplateEditor /></AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/templates/edit/:templateId" element={
          <ProtectedRoute>
            <AppLayout><TemplateEditor /></AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/campaigns" element={
          <ProtectedRoute>
            <AppLayout><Campaigns /></AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/campaigns/new" element={
          <ProtectedRoute>
            <AppLayout><CampaignBuilder /></AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/sectors" element={
          <ProtectedRoute>
            <AppLayout><Sectors /></AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/contacts" element={
          <ProtectedRoute>
            <AppLayout><Contacts /></AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/emails" element={
          <ProtectedRoute>
            <AppLayout><SentEmails /></AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/stats" element={
          <ProtectedRoute>
            <AppLayout><Statistics /></AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/my-company" element={
          <ProtectedRoute>
            <AppLayout><Clients /></AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <AppLayout><Settings /></AppLayout>
          </ProtectedRoute>
        } />
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </CreditsProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <AppContent />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
